import React, {useEffect, useState, useRef} from 'react';
import {useSelector} from 'react-redux';
import PropTypes from 'prop-types';
import {isEmpty, size} from 'lodash';
import {labels, currentResource} from '../../../../../util/utils';
import {gaasCountries, projectSessionKeys} from '../../../../../util/uiconstants';
import Utility from '../../../../../util/utilityfunctions';
import UserDate from '../../../../Common/UserDate/UserDate';
import BadgeLabel from '../../../../Common/Badge/BadgeLabel';
import ExportToExcel from '../../../../Common/ExportToExcel/ExportToExcel';
import {loadStorage, saveStorage} from '../../../../Common/localStorage';
import FilterChips from '../../../../Common/FilterChips';
import {reportingFilterId} from '../../../../../util/uiconstants';

import Select from '../../../../CoreWrappers/Select';

import MetricsTable from '../../../../Common/MetricsTable';
import {reportingCustomizeIds} from '../../../../Common/MetricsTable/columns';

const ReportingEngagementTable = (props) => {
	const maxCustomColumns = 3;
	const [numberFormat, userDateFormat] = useSelector(({userPreferences}) => [
		userPreferences.numberFormat,
		userPreferences.dateFormat
	]);
	const dashboardContext = useSelector(({dashboardContext}) => dashboardContext);
	const dashboardReportingFilters = useSelector(({dashboardFilters}) => dashboardFilters.reporting);

	const hasReportingFiltersApplied = dashboardReportingFilters != null;
	const hasEngagementCompletionFilterApplied =
		(hasReportingFiltersApplied &&
			dashboardReportingFilters.filters[0].type === reportingFilterId.completionByEngagement) ||
		false;
	const hasOpinionTypeFilterApplied =
		(hasReportingFiltersApplied && dashboardReportingFilters.filters[0].type === reportingFilterId.opinionType) ||
		false;
	const hasCompletionByLocationFilterApplied =
		(hasReportingFiltersApplied &&
			dashboardReportingFilters.filters[0].type === reportingFilterId.completionByLocation) ||
		false;

	const filterId = {
		reporting: 1,
		geography: 2
	};

	const filters = [
		{id: filterId.reporting, label: labels.reporting},
		{id: filterId.geography, label: labels.geography}
	];

	const reportingCustomizeOptions = [
		{id: reportingCustomizeIds.yearEndDateId, label: labels.yearEndDate},
		{id: reportingCustomizeIds.endDateOfPeriodCoveredId, label: labels.endDateOfPeriodCovered},
		{id: reportingCustomizeIds.reportDateId, label: labels.reportDate},
		{id: reportingCustomizeIds.archiveDeadlineId, label: labels.archiveDeadline},
		{id: reportingCustomizeIds.reportTypeId, label: labels.reportType},
		{id: reportingCustomizeIds.taskStatusId, label: labels.taskStatus}
	];

	const reportingCustomizeInitial = [
		reportingCustomizeIds.reportDateId,
		reportingCustomizeIds.archiveDeadlineId,
		reportingCustomizeIds.reportTypeId
	];

	const milestonesOptions = Utility.getMilestoneOptionWithGroup();
	const [groupAuditValue, setgroupAuditValue] = useState(null);
	const [selectedFilter, setSelectedFilter] = useState(filterId.reporting);
	const [engagementData, setEngagementData] = useState(null);
	const [showMessage, setShowMessage] = useState(false);
	const [userChange, setUserChange] = useState(false);
	const openGroups = useRef([]);
	const projectId = Utility.getProjectIdFromUrl();
	const psessionKEy = `${projectSessionKeys.projectID}_${projectId}_${projectSessionKeys.reportingSession}`;

	const isReporting = selectedFilter === filterId.reporting;

	useEffect(() => {
		let currentState = loadStorage(psessionKEy);
		if (currentState && currentState.reportingFilterid) {
			setSelectedFilter(currentState.reportingFilterid);
		}
	}, []);

	useEffect(() => {
		if (!userChange) {
			// Use engagement data.
			setgroupAuditValue(milestonesOptions[0].options[0]);
		}
	}, [engagementData]);

	useEffect(() => {
		if (hasReportingFiltersApplied) {
			const selectedTable = hasCompletionByLocationFilterApplied ? filterId.geography : filterId.reporting;
			setSelectedFilter(selectedTable);
			let currentState = loadStorage(psessionKEy) || {};
			saveStorage(psessionKEy, {...currentState, reportingFilterid: selectedTable});
		}
	}, [dashboardReportingFilters]);

	useEffect(() => {
		if (groupAuditValue?.id) {
			buildTable();
		}
	}, [props.engagementMetrics, groupAuditValue, selectedFilter, dashboardReportingFilters]);

	const loadNodes = (node, entities, data) => {
		// check if entity is selected
		let entity = entities.find((x) => x.id === node.id);

		if (entity && entity.engagements?.length > 0) {
			const items = getEngagements(entity);
			let group = {
				id: entity.id,
				name: entity.label,
				isOpen: isGroupOpened(entity.id),
				items,
				customGroupRender: () => {
					let result = <BadgeLabel label={entity.label} value={items.length} />;

					return result;
				}
			};

			group.engCount = group.items?.length;

			if (entity.isEnabled) {
				data.push(group);
			}
		}

		// load children entities for each node
		node.nodes.forEach((n) => {
			loadNodes(n, entities, data);
		});
	};

	const isGroupOpened = (id) => {
		return openGroups.current.some((x) => x === id);
	};

	const getExportDetails = () => {
		return {
			title: filters.find((f) => f.id === selectedFilter).label,
			columns: getColumnsToExport(),
			data: getDataToExport(),
			worksheetName: filters.find((f) => f.id === selectedFilter).label
		};
	};

	const getColumnsToExport = () => {
		let columns = [];
		columns.push({header: labels.orgChartEntity, key: 'entityName'});
		columns.push({header: labels.engagementId, key: 'engId'});
		columns.push({header: labels.contactName, key: 'ContactName'});
		columns.push({header: labels.contactEmail, key: 'ContactEmail'});
		columns.push({header: labels.engagementLabel, key: 'engDes'});
		if (isReporting) {
			columns.push({header: labels.yearEndDate, key: 'yearEndDate'});
			columns.push({header: labels.endDateOfPeriodCovered, key: 'endDateOfPeriodCovered'});
			columns.push({header: labels.reportDate, key: 'reportDate'});
			columns.push({header: labels.archiveDeadline, key: 'archiveDeadline'});
			columns.push({header: labels.reportType, key: 'reportType'});
		} else {
			columns.push({header: labels.geography, key: 'geography'});
			columns.push({header: labels.standars, key: 'standards'});
			columns.push({header: labels.gaap, key: 'gaap'});
		}

		columns.push({header: labels.taskStatusComplete, key: 'taskStatusComplete'});
		columns.push({header: labels.taskStatusInreview, key: 'taskStatusInreview'});
		columns.push({header: labels.taskStatusInprogress, key: 'taskStatusInprogress'});
		columns.push({header: labels.taskStatusOpen, key: 'taskStatusOpen'});
		return columns;
	};

	const getDataToExport = () => {
		let dataToExport = [];
		dashboardContext.orgChart.forEach(() => {
			if (dashboardContext && size(dashboardContext.entities) > 0) {
				dashboardContext.entities.forEach((entity) => {
					if (entity.engagements && size(entity.engagements) > 0) {
						entity.engagements.forEach((eng) => {
							let engMetricsData = props.engagementMetrics?.find((x) => x.engId === eng);
							if (engMetricsData) {
								let data = {
									entityName: entity.label,
									engId: eng,
									ContactName: '',
									ContactEmail: '',
									engDes: engMetricsData.engDes
								};
								data.taskStatusComplete = engMetricsData.allTC;
								data.taskStatusInreview = engMetricsData.allTIR;
								data.taskStatusInprogress = engMetricsData.allTIP;
								data.taskStatusOpen = engMetricsData.allTO;

								if (isReporting) {
									data.yearEndDate = Utility.isValidDate(engMetricsData.cliYearEndDate)
										? Utility.getFormattedDate(engMetricsData.cliYearEndDate, userDateFormat)
										: '';
									data.endDateOfPeriodCovered = Utility.isValidDate(engMetricsData.engPerEndDate)
										? Utility.getFormattedDate(engMetricsData.engPerEndDate, userDateFormat)
										: '';
									data.reportDate = Utility.isValidDate(engMetricsData.relDate)
										? Utility.getFormattedDate(engMetricsData.relDate, userDateFormat)
										: '';
									data.archiveDeadline = Utility.isValidDate(engMetricsData.arcDeadDate)
										? Utility.getFormattedDate(engMetricsData.arcDeadDate, userDateFormat)
										: '';
									data.reportType = currentResource.archiveReportQualifiedType.find(
										(x) => x.id === engMetricsData.arcRepQlfType
									)?.label;
								} else {
									data.geography = engMetricsData.countryName;
									data.standards = gaasCountries.find(
										(x) => x.profileAnswerOptionId === engMetricsData.pGaas
									)?.profileAnswerOptionValue;
									data.gaap = getGaap(engMetricsData);
								}
								dataToExport.push(data);
							}
						});
					}
				});
			}
		});
		return dataToExport;
	};

	const getEngagements = (entity) => {
		let engagements = entity.engagements.reduce((filtered, engagementId) => {
			if (!isEmpty(props.engagementMetrics)) {
				const engagement = {...props.engagementMetrics.find((x) => x.engId === engagementId)};
				engagement.tableClass = getTableClass();

				filtered.push(engagement);
			}

			return filtered;
		}, []);

		if (hasReportingFiltersApplied) {
			engagements = engagements.filter(({engId}) => dashboardReportingFilters?.engagements?.includes(engId));
		}

		return engagements;
	};

	const buildTable = () => {
		// check if selected entities have engagements
		if (dashboardContext.entities?.every((entity) => entity.engagements?.length === 0)) {
			setShowMessage(true);
		} else {
			let data = [];

			// show entities base on org chart order and if it was selected
			dashboardContext.orgChart.forEach((node) => {
				loadNodes(node, dashboardContext.entities, data);
			});

			setShowMessage(false);

			// when searching, remove entity without engagement
			if (hasReportingFiltersApplied) {
				data = data?.filter((x) => x?.items?.length !== 0);
			}

			setEngagementData(data);
		}
	};

	const getTableClass = () => {
		return isReporting ? 'reporting-table' : 'geography-table';
	};

	const getFormattedDate = (date) => {
		const userDate = <UserDate date={date} />;
		return userDate;
	};

	const getGaap = (item) => {
		let result = '';

		// checks whether the value is zero or or null/undefined
		if (item.pGaapIfrs) {
			result = labels.ifrs;
		} else if (item.pGaapUS) {
			result = labels.pGaapUS;
		} else if (item.pGaapOther) {
			result = labels.other;
		}

		return result;
	};

	const renderCustomDetails = () => {
		if (dashboardReportingFilters?.filters) {
			const filterChipsProps = {
				filters: [...dashboardReportingFilters?.filters],
				onClearFilter: dashboardReportingFilters?.onClearFilter
			};

			return <FilterChips {...filterChipsProps} />;
		}
	};

	const handlerOnSwitchChange = (id) => {
		let _id = Number.parseInt(id);
		setSelectedFilter(_id);
		setUserChange(true);
		buildTable();

		let currentState = loadStorage(psessionKEy);
		if (!currentState) {
			currentState = {};
		}
		currentState.reportingFilterid = _id;
		saveStorage(psessionKEy, currentState);
	};

	const handlerOnGroupChange = (group, isOpen) => {
		setUserChange(true);
		if (isOpen) {
			openGroups.current.push(group.id);
		} else {
			openGroups.current = openGroups.current.filter((x) => x !== group.id);
		}
	};

	const renderTableSelect = () => {
		const selectProps = {
			value: selectedFilter,
			wrapperClass: 'table-select',
			options: filters,
			onChange: handlerOnSwitchChange
		};

		return <Select {...selectProps} />;
	};

	const renderNewTable = () => {
		const tableProps = {
			data: engagementData,
			variant: Object.keys(filterId).find((key) => {
				if (filterId[key] === selectedFilter) return key;
			}),
			renderHeader: (renderCustomizeColumns) => (
				<>
					{isReporting && renderCustomizeColumns(labels.selectUpToThreeOptionHeader)}
					{renderTableSelect()}
					<ExportToExcel getDataToExport={getExportDetails} className="export-data" />
				</>
			),
			renderFilter: renderCustomDetails
		};

		if (isReporting) {
			tableProps.columnVisibilityOptions = reportingCustomizeOptions;
			tableProps.columnVisibility = reportingCustomizeInitial;
			tableProps.maxCustomColumns = maxCustomColumns;
		}

		return <MetricsTable {...tableProps} />;
	};

	return renderNewTable();
};

ReportingEngagementTable.defaultProps = {
	engagementMetrics: []
};

ReportingEngagementTable.propTypes = {
	engagementMetrics: PropTypes.arrayOf(
		PropTypes.shape({
			cRAccepted: PropTypes.number,
			cRDue3: PropTypes.number,
			cRDue5: PropTypes.number,
			cRDue10: PropTypes.number,
			cRDue30: PropTypes.number,
			cRDueTom: PropTypes.number,
			cRNotSent: PropTypes.number,
			cROD: PropTypes.number,
			cRPendResp: PropTypes.number,
			cRReceived: PropTypes.number,
			cRRetrn: PropTypes.number,
			cRTotal: PropTypes.number,
			cTComplSend: PropTypes.number,
			cTNotSent: PropTypes.number,
			cTOD: PropTypes.number,
			cTPartialSend: PropTypes.number,
			cTRetrn: PropTypes.number,
			cTTotal: PropTypes.number,
			cTUnlinkTot: PropTypes.number,
			conTaskClo: PropTypes.number,
			conTaskInProg: PropTypes.number,
			conTaskInRev: PropTypes.number,
			conTaskOpen: PropTypes.number,
			conTaskTotal: PropTypes.number,
			conTested: PropTypes.number,
			conTotal: PropTypes.number,
			defDesign: PropTypes.number,
			defOper: PropTypes.number,
			defOperAndDes: PropTypes.number,
			engDes: PropTypes.string,
			engId: PropTypes.number,
			engKey: PropTypes.number,
			gAType: PropTypes.number,
			hasAPEQRSO: PropTypes.bool,
			hasAPPICSO: PropTypes.bool,
			hasCRAEQRSO: PropTypes.bool,
			hasCRAPICSO: PropTypes.bool,
			isEQRCRASODlyd: PropTypes.bool,
			isEQREng: PropTypes.bool,
			isEQRInv: PropTypes.bool,
			isPartAPSODlyd: PropTypes.bool,
			isPartCRASODlyd: PropTypes.bool,
			isPartInvDlyd: PropTypes.bool
		})
	)
};

export default ReportingEngagementTable;
